import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { VStack, Heading, Text, Spinner, Button, Circle } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { getSubscriptionStatus } from '../api/subscriptionApi';

/**
 * Provider-agnostic checkout return page. The hosted provider (Stripe today,
 * WayForPay later) redirects here after payment. Rather than parse
 * provider-specific return params, the success path polls the subscription
 * `status` query — which the billing webhook flips to `active` — so it works
 * identically for every provider. Mounted full-screen at /billing/success and
 * /billing/cancel.
 */

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

export function BillingReturnPage({ outcome }: { outcome: 'success' | 'cancel' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isSuccess = outcome === 'success';
  const [startedAt] = useState(() => Date.now());

  const statusQuery = useQuery({
    queryKey: ['subscription', 'status'],
    queryFn: getSubscriptionStatus,
    enabled: isSuccess,
    refetchInterval: (query) =>
      query.state.data?.status === 'active' ? false : POLL_INTERVAL_MS,
  });

  const activated = statusQuery.data?.status === 'active';
  const timedOut = !activated && Date.now() - startedAt > POLL_TIMEOUT_MS;

  // On confirmed activation, hand the user back to the subscription screen.
  useEffect(() => {
    if (!activated) return;
    const id = setTimeout(() => navigate('/subscription'), 1800);
    return () => clearTimeout(id);
  }, [activated, navigate]);

  const wrap = (children: ReactNode) => (
    <VStack minH="100vh" justify="center" align="center" p={6} spacing={6} bg="white">
      {children}
    </VStack>
  );

  if (!isSuccess) {
    return wrap(
      <>
        <Circle size="80px" bg="orange.50">
          <Text fontSize="2xl">↩</Text>
        </Circle>
        <Heading size="lg" textAlign="center">
          {t('subscription.return.cancelTitle')}
        </Heading>
        <Text fontSize="md" color="gray.500" textAlign="center" maxW="420px">
          {t('subscription.return.cancelBody')}
        </Text>
        <Button colorScheme="brand" onClick={() => navigate('/subscription')}>
          {t('subscription.return.backToPlans')}
        </Button>
      </>,
    );
  }

  if (activated) {
    return wrap(
      <>
        <Circle size="80px" bg="green.50">
          <Text fontSize="2xl">✓</Text>
        </Circle>
        <Heading size="lg" textAlign="center">
          {t('subscription.return.successTitle')}
        </Heading>
        <Text fontSize="md" color="gray.500" textAlign="center">
          {t('subscription.return.successBody')}
        </Text>
      </>,
    );
  }

  if (timedOut) {
    return wrap(
      <>
        <Circle size="80px" bg="blue.50">
          <Text fontSize="2xl">⏳</Text>
        </Circle>
        <Heading size="lg" textAlign="center">
          {t('subscription.return.pendingTitle')}
        </Heading>
        <Text fontSize="md" color="gray.500" textAlign="center" maxW="420px">
          {t('subscription.return.pendingBody')}
        </Text>
        <Button colorScheme="brand" onClick={() => navigate('/subscription')}>
          {t('subscription.return.goToSubscription')}
        </Button>
      </>,
    );
  }

  return wrap(
    <>
      <Spinner size="xl" color="brand.500" thickness="3px" />
      <Text fontSize="lg" color="gray.500">
        {t('subscription.return.confirming')}
      </Text>
    </>,
  );
}

export default BillingReturnPage;
